// src/components/ui/Carousel.tsx
import React, { useState, useEffect, useCallback, Children } from 'react';
import { motion } from 'framer-motion';import { ChevronLeft, ChevronRight, Pause, Play, Image as ImageIcon } from 'lucide-react';
import { useViewportSize } from '../../hooks/useScrollAnimation';

interface CarouselProps {
  children: React.ReactNode;
  autoPlay?: boolean;
  interval?: number;
  showControls?: boolean;
  showDots?: boolean;
  showArrows?: boolean;
  className?: string;
}

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt?: string;
  className?: string;
}

const Carousel: React.FC<CarouselProps> = ({
  children,
  autoPlay = true,
  interval = 5000,
  showControls = true,
  showDots = true,
  showArrows = true,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [errorImages, setErrorImages] = useState<Set<number>>(new Set());
  const { width } = useViewportSize();
  
  const items = Children.toArray(children);
  const totalItems = items.length;

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalItems);
  }, [totalItems]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalItems) % totalItems);
  }, [totalItems]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Handle image loading errors
  const handleImageError = useCallback((index: number) => {
    console.warn(`Failed to load image at index ${index}`);
    setErrorImages(prev => new Set(prev).add(index));
  }, []);

  // Auto play effect
  useEffect(() => {
    if (!isPlaying || !autoPlay || totalItems <= 1) return;

    const timer = setInterval(() => {
      nextSlide();
    }, interval);

    return () => clearInterval(timer);
  }, [isPlaying, autoPlay, interval, nextSlide, totalItems]);

  // Reset playing state on touch devices
  useEffect(() => {
    const handleTouchStart = () => {
      if (isPlaying) {
        setIsPlaying(false);
      }
    };

    window.addEventListener('touchstart', handleTouchStart);
    return () => window.removeEventListener('touchstart', handleTouchStart);
  }, [isPlaying]);

  // Determine number of visible items based on screen size
  const getVisibleCount = () => {
    if (width >= 1024) return 3; // Desktop
    if (width >= 768) return 2;  // Tablet
    return 1;                    // Mobile
  };

  const visibleCount = getVisibleCount();
  const maxIndex = Math.max(0, totalItems - visibleCount);

  // Adjust current index if it's beyond max with current visible count
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [currentIndex, maxIndex, width]);

  // Don't render carousel if no items
  if (totalItems === 0) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-center text-mauve-600">
          <ImageIcon size={48} className="mx-auto mb-4 text-mauve-300" />
          <p>No images to display</p>
        </div>
      </div>
    );
  }

  // Wrap children to handle image errors
  const wrappedItems = items.map((item, index) => {
    // Check if this is an image element
    if (React.isValidElement<ImageProps>(item) && item.type === 'img') {
      const props = item.props as ImageProps;
      const isError = errorImages.has(index);
      
      if (isError) {
        return (
          <div
            key={index}
            className={`aspect-square rounded-2xl overflow-hidden bg-mauve-100 flex items-center justify-center ${props.className || ''}`}
            style={{ minHeight: '200px' }}
          >
            <div className="text-center p-4">
              <ImageIcon size={32} className="mx-auto mb-2 text-mauve-300" />
              <p className="text-mauve-500 text-sm">Image not available</p>
            </div>
          </div>
        );
      }

      // Create a wrapper div for the image to handle errors
      return (
        <div key={index} className="relative">
          <img
            src={props.src}
            alt={props.alt || `Carousel image ${index + 1}`}
            className={`w-full h-auto rounded-2xl ${props.className || ''}`}
            onError={() => handleImageError(index)}
          />
        </div>
      );
    }
    
    return item;
  });

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Carousel Container */}
      <div className="relative">
        <motion.div
          className="flex transition-transform duration-300 ease-out"
          style={{
            transform: `translateX(-${currentIndex * (100 / visibleCount)}%)`,
          }}
          animate={{
            x: `-${currentIndex * (100 / visibleCount)}%`,
          }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {wrappedItems.map((item, index) => (
            <div
              key={index}
              className="flex-shrink-0 px-2"
              style={{ width: `${100 / visibleCount}%` }}
            >
              {item}
            </div>
          ))}
        </motion.div>
      </div>

      {/* Navigation Arrows */}
      {showArrows && totalItems > visibleCount && (
        <>
          <button
            onClick={prevSlide}
            disabled={currentIndex === 0}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all z-10 backdrop-blur-sm"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} className="text-mauve-700" />
          </button>
          
          <button
            onClick={nextSlide}
            disabled={currentIndex >= maxIndex}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all z-10 backdrop-blur-sm"
            aria-label="Next slide"
          >
            <ChevronRight size={20} className="text-mauve-700" />
          </button>
        </>
      )}

      {/* Controls */}
      {showControls && totalItems > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          {/* Play/Pause Button */}
          {autoPlay && totalItems > 1 && (
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="p-2 rounded-full bg-mauve-100 hover:bg-mauve-200 transition-colors"
              aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
            >
              {isPlaying ? (
                <Pause size={18} className="text-mauve-700" />
              ) : (
                <Play size={18} className="text-mauve-700" />
              )}
            </button>
          )}

          {/* Dots Indicator */}
          {showDots && (
            <div className="flex items-center gap-2">
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex
                      ? 'bg-mauve-500 w-6'
                      : 'bg-mauve-300 hover:bg-mauve-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Counter */}
          <div className="text-sm text-mauve-600 font-medium">
            {currentIndex + 1} / {maxIndex + 1}
          </div>
        </div>
      )}
    </div>
  );
};

export default Carousel;